<template>
  <v-dialog :value="value" max-width="560" @input="$emit('input', $event)">
    <v-card>
      <v-card-title>{{ $t("STOCK.DELIVERY_NOTES.DIRECT_SALE_TITLE") }}</v-card-title>
      <v-card-text>
        <p class="mb-4">
          {{ mode === "purchase" ? $t("STOCK.DELIVERY_NOTES.DIRECT_SALE_FROM_PO") : $t("STOCK.DELIVERY_NOTES.DIRECT_SALE_FROM_SO") }}
        </p>
        <v-alert v-if="!ready" type="info" dense text>
          {{ $t("STOCK.DELIVERY_NOTES.DIRECT_SALE_SAVE_FIRST") }}
        </v-alert>
        <template v-else>
          <v-autocomplete
            v-if="mode === 'purchase'"
            v-model="salesOrderItemId"
            :items="salesLines"
            :item-text="salesLineLabel"
            item-value="id"
            :label="$t('STOCK.DELIVERY_NOTES.DIRECT_SALE_PICK_SO')"
            :loading="loading"
            clearable
          />
          <v-autocomplete
            v-else
            v-model="purchaseScheduleId"
            :items="purchaseSchedules"
            :item-text="purchaseLineLabel"
            item-value="id"
            :label="$t('STOCK.DELIVERY_NOTES.DIRECT_SALE_PICK_PO')"
            :loading="loading"
            clearable
          />
          <v-text-field
            v-model.number="quantity"
            type="number"
            :label="$t('STOCK.DELIVERY_NOTES.DIRECT_SALE_QTY')"
            min="0"
          />
          <v-alert v-if="!loading && optionsEmpty" type="info" dense text>
            {{ $t("STOCK.DELIVERY_NOTES.DIRECT_SALE_EMPTY") }}
          </v-alert>
          <v-alert v-if="errorMessage" type="error" dense text>{{ errorMessage }}</v-alert>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="$emit('input', false)">{{ $t("GENERAL.CANCEL") }}</v-btn>
        <v-btn color="success" :disabled="!canSubmit" :loading="saving" @click="submit">
          {{ $t("STOCK.DELIVERY_NOTES.DIRECT_SALE_TITLE") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { CREATE_DIRECT_SALE, GET_DIRECT_SALE_OPTIONS } from "@/store/stock/stockDeliveryNotes.module";
import { formatStockQuantity } from "@/common/stock/stockNumberFormat";

export default {
  name: "StockDirectSaleDialog",
  props: {
    value: { type: Boolean, default: false },
    mode: { type: String, default: "purchase" },
    organizationId: { type: [Number, String], default: null },
    stockProductId: { type: [Number, String], default: null },
    purchaseOrderItemScheduleId: { type: [Number, String], default: null },
    salesOrderItemIdProp: { type: [Number, String], default: null },
    defaultQuantity: { type: [Number, String], default: null },
  },
  data() {
    return {
      loading: false,
      saving: false,
      salesLines: [],
      purchaseSchedules: [],
      salesOrderItemId: null,
      purchaseScheduleId: null,
      quantity: null,
      errorMessage: null,
    };
  },
  computed: {
    ready() {
      return this.mode === "purchase"
        ? !!this.purchaseOrderItemScheduleId
        : !!this.salesOrderItemIdProp;
    },
    optionsEmpty() {
      return this.mode === "purchase"
        ? this.salesLines.length === 0
        : this.purchaseSchedules.length === 0;
    },
    canSubmit() {
      if (!this.ready || this.saving || !(Number(this.quantity) > 0)) {
        return false;
      }
      return this.mode === "purchase"
        ? !!this.salesOrderItemId
        : !!this.purchaseScheduleId;
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.errorMessage = null;
        this.quantity = this.defaultQuantity != null ? Number(this.defaultQuantity) : null;
        this.salesOrderItemId = null;
        this.purchaseScheduleId = null;
        this.loadOptions();
      }
    },
  },
  methods: {
    salesLineLabel(line) {
      return `${line.sales_order?.code || ""} · ${line.customer || ""} · ${formatStockQuantity(line.unplanned_quantity)}`;
    },
    purchaseLineLabel(line) {
      return `${line.purchase_order?.code || ""} · ${line.supplier || ""} · ${formatStockQuantity(line.quantity)} · ${line.eta_date || ""}`;
    },
    async loadOptions() {
      if (!this.organizationId || !this.stockProductId) {
        this.salesLines = [];
        this.purchaseSchedules = [];
        return;
      }
      this.loading = true;
      try {
        const data = await this.$store.dispatch(GET_DIRECT_SALE_OPTIONS, {
          organization_id: this.organizationId,
          stock_product_id: this.stockProductId,
        });
        this.salesLines = data?.sales_lines || [];
        this.purchaseSchedules = data?.purchase_schedules || [];
      } catch (error) {
        this.errorMessage = error?.message || this.$t("GENERAL.UNEXPECTED_ERROR");
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      this.saving = true;
      this.errorMessage = null;
      try {
        const data = await this.$store.dispatch(CREATE_DIRECT_SALE, {
          purchase_order_item_schedule_id: this.mode === "purchase"
            ? this.purchaseOrderItemScheduleId
            : this.purchaseScheduleId,
          sales_order_item_id: this.mode === "purchase"
            ? this.salesOrderItemId
            : this.salesOrderItemIdProp,
          quantity: this.quantity,
        });
        this.$emit("saved", data);
        this.$emit("input", false);
      } catch (error) {
        const first = error?.errors && Object.values(error.errors).flat()[0];
        this.errorMessage = first || error?.message || this.$t("GENERAL.UNEXPECTED_ERROR");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
